import { useState, useEffect, useRef } from 'react';
import { getModelos, createModelo, updateModelo, deleteModelo, getBases, getHilos, uploadFichaModelo, deleteFichaModelo, getFileUrl, getMuestrasBase, getMarcas, getTiposProducto, getEntalles, getTelas, reorderModelos, downloadModeloFiles } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Search, Plus, Pencil, Trash2, Upload, Download, FileText, X, FolderOpen, Scissors, RotateCcw, Info, GripVertical, Check, FolderDown } from 'lucide-react';
import { ResizableTableHead, ResizableTableCell, useResizableColumns } from '../components/ResizableTable';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component for drag and drop
const SortableModeloRow = ({ item, children }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragging ? '#f1f5f9' : undefined,
    };

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            className="border-b border-slate-100 hover:bg-slate-50"
        >
            <TableCell className="py-3 px-2 w-10">
                <button
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
                >
                    <GripVertical className="h-4 w-4 text-slate-400" />
                </button>
            </TableCell>
            {children}
        </TableRow>
    );
};

function ModelosPage() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({});
    const [bases, setBases] = useState([]);
    const [hilos, setHilos] = useState([]);
    const [muestrasBase, setMuestrasBase] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [tiposProducto, setTiposProducto] = useState([]);
    const [entalles, setEntalles] = useState([]);
    const [telas, setTelas] = useState([]);
    const [fichasDialogOpen, setFichasDialogOpen] = useState(false);
    const [currentModelo, setCurrentModelo] = useState(null);
    const [fichaFile, setFichaFile] = useState(null);
    const [fichaName, setFichaName] = useState('');
    const [uploadingFicha, setUploadingFicha] = useState(false);
    const fichaInputRef = useRef(null);
    const [showNewFichaForm, setShowNewFichaForm] = useState(false);
    const [fichaSearch, setFichaSearch] = useState('');
    
    // Base selector modal
    const [baseModalOpen, setBaseModalOpen] = useState(false);
    const [baseSearch, setBaseSearch] = useState('');
    
    // Columnas redimensionables
    const defaultColumnWidths = {
        modelo: 150,
        clasificacion: 200,
        hilo: 100,
        patron: 80,
        fichasGenerales: 120,
        fichasModelo: 120,
        tizados: 100,
        estado: 100
    };
    const { columnWidths, updateWidth, resetWidths } = useResizableColumns('modelos', defaultColumnWidths);
    
    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = data.findIndex(item => item.id === active.id);
            const newIndex = data.findIndex(item => item.id === over.id);
            const newData = arrayMove(data, oldIndex, newIndex);
            setData(newData);
            
            const reorderItems = newData.map((item, index) => ({
                id: item.id,
                orden: index + 1
            }));
            
            try {
                await reorderModelos(reorderItems);
            } catch (error) {
                toast.error('Error al reordenar');
                fetchData();
            }
        }
    };
    
    // Dialogs for viewing base's files
    const [baseFichasDialog, setBaseFichasDialog] = useState(false);
    const [baseTizadosDialog, setBaseTizadosDialog] = useState(false);
    const [viewingModelo, setViewingModelo] = useState(null);
    const [tizadoSearch, setTizadoSearch] = useState('');

    // Filter tizados by search
    const filteredTizados = (viewingModelo?.base_tizados || []).filter(t => {
        if (!tizadoSearch) return true;
        const searchLower = tizadoSearch.toLowerCase();
        const anchoStr = t.ancho ? t.ancho.toString() : '';
        const curvaStr = t.curva || '';
        // Get bases names for this tizado
        const basesNames = (t.bases_ids || []).map(bid => {
            const base = bases.find(b => b.id === bid);
            return base ? base.nombre.toLowerCase() : '';
        }).join(' ');
        return anchoStr.includes(searchLower) || 
               curvaStr.toLowerCase().includes(searchLower) ||
               basesNames.includes(searchLower);
    });

    // Get other bases names for a tizado (excluding current base)
    const getOtherBasesForTizado = (tizado) => {
        if (!tizado.bases_ids) return [];
        return tizado.bases_ids
            .filter(bid => bid !== viewingModelo?.base_id)
            .map(bid => {
                const base = bases.find(b => b.id === bid);
                return base ? base.nombre : bid.slice(-4);
            });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getModelos({ search });
            setData(res.data);
        } catch (err) {
            toast.error('Error al cargar');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        getBases({ activo: true }).then(res => setBases(res.data)).catch(() => {});
        getHilos({ activo: true }).then(res => setHilos(res.data)).catch(() => {});
        getMuestrasBase({}).then(res => setMuestrasBase(res.data)).catch(() => {});
        getMarcas({ activo: true }).then(res => setMarcas(res.data)).catch(() => {});
        getTiposProducto({ activo: true }).then(res => setTiposProducto(res.data)).catch(() => {});
        getEntalles({ activo: true }).then(res => setEntalles(res.data)).catch(() => {});
        getTelas({ activo: true }).then(res => setTelas(res.data)).catch(() => {});
    }, []);

    const getBaseName = (id) => bases.find(b => b.id === id)?.nombre || '-';
    const getHiloName = (id) => hilos.find(h => h.id === id)?.nombre || '-';
    
    // Get full info of selected base
    const getSelectedBaseInfo = () => {
        if (!formData.base_id) return null;
        const base = bases.find(b => b.id === formData.base_id);
        if (!base) return null;
        
        const muestra = muestrasBase.find(m => m.id === base.muestra_base_id);
        if (!muestra) return { baseName: base.nombre };
        
        return {
            baseName: base.nombre,
            marca: marcas.find(m => m.id === muestra.marca_id)?.nombre || '-',
            tipo: tiposProducto.find(t => t.id === muestra.tipo_producto_id)?.nombre || '-',
            entalle: entalles.find(e => e.id === muestra.entalle_id)?.nombre || '-',
            tela: telas.find(t => t.id === muestra.tela_id)?.nombre || '-',
            nMuestra: muestra.n_muestra || '-'
        };
    };

    const handleAdd = () => {
        setSelectedItem(null);
        setFormData({ aprobado: false });
        setFormOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setFormData({ nombre: item.nombre, base_id: item.base_id, hilo_id: item.hilo_id, aprobado: item.aprobado });
        setFormOpen(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!formData.base_id) { toast.error('Selecciona una base'); return; }
        setSubmitting(true);
        try {
            if (selectedItem) {
                await updateModelo(selectedItem.id, formData);
                toast.success('Actualizado');
            } else {
                await createModelo(formData);
                toast.success('Creado');
            }
            setFormOpen(false);
            fetchData();
        } catch (err) {
            toast.error('Error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteModelo(selectedItem.id);
            toast.success('Eliminado');
            setDeleteOpen(false);
            fetchData();
        } catch (err) {
            toast.error('Error');
        }
    };

    const openFichasDialog = (modelo) => {
        setCurrentModelo(modelo);
        setFichasDialogOpen(true);
        setFichaFile(null);
        setFichaName('');
        setShowNewFichaForm(false);
        setFichaSearch('');
    };

    const handleUploadFicha = async () => {
        if (!fichaFile || !currentModelo || !fichaName) return;
        setUploadingFicha(true);
        try {
            await uploadFichaModelo(currentModelo.id, [fichaFile], [fichaName]);
            toast.success('Ficha creada exitosamente');
            setFichaFile(null);
            setFichaName('');
            setShowNewFichaForm(false);
            fetchData();
            const res = await getModelos({});
            setCurrentModelo(res.data.find(m => m.id === currentModelo.id));
        } catch (err) {
            toast.error('Error al crear ficha');
        } finally {
            setUploadingFicha(false);
        }
    };

    // Helper functions for file display
    const getFileName = (path) => {
        if (!path) return '';
        const parts = path.split('/');
        return parts[parts.length - 1];
    };

    const getFileExtension = (path) => {
        if (!path) return '';
        const name = getFileName(path);
        const parts = name.split('.');
        return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
    };

    // Filtered fichas for search
    const filteredFichasModelo = () => {
        if (!currentModelo?.fichas_archivos) return [];
        const fichas = currentModelo.fichas_archivos.map((archivo, i) => ({
            archivo,
            nombre: currentModelo.fichas_nombres?.[i] || `Ficha ${i + 1}`,
            index: i
        }));
        if (!fichaSearch) return fichas;
        return fichas.filter(f => f.nombre.toLowerCase().includes(fichaSearch.toLowerCase()));
    };

    const handleDeleteFicha = async (index) => {
        try {
            await deleteFichaModelo(currentModelo.id, index);
            toast.success('Eliminada');
            fetchData();
            const res = await getModelos({});
            setCurrentModelo(res.data.find(m => m.id === currentModelo.id));
        } catch (err) {
            toast.error('Error');
        }
    };

    const handleDownloadAll = (modelo) => {
        const url = downloadModeloFiles(modelo.id);
        window.open(url, '_blank');
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Modelos</h1>
                    <p className="text-slate-500 text-sm mt-1">Gestiona los modelos vinculados a bases</p>
                </div>
                <Button onClick={handleAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />Nuevo Modelo
                </Button>
            </div>

            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Buscar..." value={search} onChange={(e) => { setSearch(e.target.value); fetchData(); }} className="pl-10 bg-white" />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="flex justify-end p-2 border-b border-slate-100">
                    <Button variant="ghost" size="sm" onClick={resetWidths} className="text-slate-500 hover:text-slate-700">
                        <RotateCcw className="w-3 h-3 mr-1" />Restaurar anchos
                    </Button>
                </div>
                <div className="overflow-x-auto">
                    <p className="text-xs text-slate-500 mb-2 ml-4 flex items-center gap-1">
                        <GripVertical className="w-3 h-3" /> Arrastra las filas para reordenar
                    </p>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50">
                                    <TableHead className="w-10"></TableHead>
                                    <ResizableTableHead columnKey="modelo" width={columnWidths.modelo} onResize={updateWidth}>Modelo</ResizableTableHead>
                                    <ResizableTableHead columnKey="clasificacion" width={columnWidths.clasificacion} onResize={updateWidth}>Clasificación</ResizableTableHead>
                                    <ResizableTableHead columnKey="hilo" width={columnWidths.hilo} onResize={updateWidth}>Hilo</ResizableTableHead>
                                    <ResizableTableHead columnKey="patron" width={columnWidths.patron} onResize={updateWidth}>Patrón</ResizableTableHead>
                                    <ResizableTableHead columnKey="fichasGenerales" width={columnWidths.fichasGenerales} onResize={updateWidth}>Fichas Generales</ResizableTableHead>
                                    <ResizableTableHead columnKey="fichasModelo" width={columnWidths.fichasModelo} onResize={updateWidth}>Fichas Modelo</ResizableTableHead>
                                    <ResizableTableHead columnKey="tizados" width={columnWidths.tizados} onResize={updateWidth}>Tizados</ResizableTableHead>
                                    <ResizableTableHead columnKey="estado" width={columnWidths.estado} onResize={updateWidth}>Estado</ResizableTableHead>
                                    <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-3 px-4 sticky right-0 bg-slate-50 z-10" style={{ minWidth: '130px' }}>Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <SortableContext items={data.map(d => d.id)} strategy={verticalListSortingStrategy}>
                                <TableBody>
                                    {loading ? (
                                        <TableRow><TableCell colSpan={10} className="text-center py-8">Cargando...</TableCell></TableRow>
                                    ) : data.length === 0 ? (
                                        <TableRow><TableCell colSpan={10} className="text-center py-8">No hay modelos</TableCell></TableRow>
                                    ) : data.map((item) => (
                                        <SortableModeloRow key={item.id} item={item}>
                                            <ResizableTableCell width={columnWidths.modelo}>
                                                <span className="font-medium">{getBaseName(item.base_id)}</span>
                                            </ResizableTableCell>
                                            <ResizableTableCell width={columnWidths.clasificacion}>
                                                <span className="text-slate-600">{item.clasificacion || item.muestra_base_nombre || '-'}</span>
                                            </ResizableTableCell>
                                            <ResizableTableCell width={columnWidths.hilo}>
                                                {getHiloName(item.hilo_id)}
                                            </ResizableTableCell>
                                            <ResizableTableCell width={columnWidths.patron}>
                                                {item.base_patron_archivo ? (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={() => window.open(getFileUrl(item.base_patron_archivo), '_blank')}
                                                        className="text-blue-600 hover:text-blue-800 h-7 text-xs"
                                                    >
                                                        <Download className="w-3 h-3 mr-1" />Ver
                                                    </Button>
                                                ) : (
                                                    <span className="text-slate-400">-</span>
                                                )}
                                            </ResizableTableCell>
                                            <ResizableTableCell width={columnWidths.fichasGenerales}>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => { setViewingModelo(item); setBaseFichasDialog(true); }} 
                                                    className="text-purple-600 hover:text-purple-800 h-7 text-xs"
                                                >
                                                    <FolderOpen className="w-3 h-3 mr-1" />{item.base_fichas_archivos?.length || 0}
                                                </Button>
                                            </ResizableTableCell>
                                            <ResizableTableCell width={columnWidths.fichasModelo}>
                                                <Button variant="ghost" size="sm" onClick={() => openFichasDialog(item)} className="text-blue-600 h-7 text-xs">
                                                    <FileText className="w-3 h-3 mr-1" />{item.fichas_archivos?.length || 0}
                                                </Button>
                                            </ResizableTableCell>
                                            <ResizableTableCell width={columnWidths.tizados}>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => { setViewingModelo(item); setBaseTizadosDialog(true); }} 
                                                    className="text-orange-600 hover:text-orange-800 h-7 text-xs"
                                                >
                                                    <Scissors className="w-3 h-3 mr-1" />{item.base_tizados?.length || 0}
                                                </Button>
                                            </ResizableTableCell>
                                            <ResizableTableCell width={columnWidths.estado}>
                                                <Badge className={item.aprobado ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                                    {item.aprobado ? 'Aprobado' : 'Pendiente'}
                                                </Badge>
                                            </ResizableTableCell>
                                            <TableCell className="py-3 px-4 sticky right-0 bg-white z-10" style={{ minWidth: '130px' }}>
                                                <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => handleDownloadAll(item)} title="Descargar archivos" data-testid={`download-modelo-${item.id}`}><FolderDown className="h-4 w-4 text-emerald-600" /></Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Pencil className="h-4 w-4" /></Button>
                                                    <Button variant="ghost" size="sm" onClick={() => { setSelectedItem(item); setDeleteOpen(true); }} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                                </div>
                                            </TableCell>
                                        </SortableModeloRow>
                                    ))}
                                </TableBody>
                            </SortableContext>
                        </Table>
                    </DndContext>
                </div>
            </div>

            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="sm:max-w-lg bg-white">
                    <DialogHeader>
                        <DialogTitle>{selectedItem ? 'Editar' : 'Nuevo'} Modelo</DialogTitle>
                        <DialogDescription className="sr-only">Form</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleFormSubmit} className="space-y-4 py-4">
                        <div>
                            <Label>Base (Modelo) * <span className="text-slate-400 text-xs">(solo aprobadas)</span></Label>
                            <div 
                                onClick={() => setBaseModalOpen(true)}
                                className="w-full min-h-[44px] px-3 py-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between"
                            >
                                {formData.base_id ? (
                                    <span className="text-sm text-slate-700 leading-tight">
                                        {getBaseName(formData.base_id)}
                                    </span>
                                ) : (
                                    <span className="text-slate-400 text-sm">Clic para seleccionar base...</span>
                                )}
                                <Search className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                            </div>
                        </div>
                        
                        {/* Panel informativo de la base seleccionada */}
                        {formData.base_id && getSelectedBaseInfo() && (
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                                <div className="flex items-center gap-2 text-slate-600 text-sm font-medium border-b border-slate-200 pb-2 mb-2">
                                    <Info className="w-4 h-4" />
                                    Información de la Base
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">N° Muestra:</span>
                                        <span className="font-medium text-slate-700">{getSelectedBaseInfo()?.nMuestra}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Marca:</span>
                                        <span className="font-medium text-slate-700">{getSelectedBaseInfo()?.marca}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Tipo:</span>
                                        <span className="font-medium text-slate-700">{getSelectedBaseInfo()?.tipo}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Entalle:</span>
                                        <span className="font-medium text-slate-700">{getSelectedBaseInfo()?.entalle}</span>
                                    </div>
                                    <div className="col-span-2 flex justify-between">
                                        <span className="text-slate-500">Tela:</span>
                                        <span className="font-medium text-slate-700">{getSelectedBaseInfo()?.tela}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div>
                            <Label>Hilo</Label>
                            <Select value={formData.hilo_id || ''} onValueChange={(v) => setFormData({...formData, hilo_id: v})}>
                                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                <SelectContent>{hilos.map(h => <SelectItem key={h.id} value={h.id}>{h.nombre}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <input type="checkbox" checked={formData.aprobado || false} onChange={(e) => setFormData({...formData, aprobado: e.target.checked})} />
                            <Label>Aprobado</Label>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>{submitting ? '...' : 'Guardar'}</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="sm:max-w-md bg-white">
                    <DialogHeader><DialogTitle>Eliminar</DialogTitle><DialogDescription className="sr-only">Confirm</DialogDescription></DialogHeader>
                    <p className="py-4">¿Eliminar modelo "{getBaseName(selectedItem?.base_id)}"?</p>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleConfirmDelete}>Eliminar</Button>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Base Selector Modal */}
            <Dialog open={baseModalOpen} onOpenChange={setBaseModalOpen}>
                <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader className="border-b border-slate-200 pb-4">
                        <DialogTitle className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                            Seleccionar Base
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-500 mt-1">
                            Solo se muestran bases aprobadas
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4 flex-1 overflow-hidden flex flex-col">
                        {/* Buscador */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input 
                                placeholder="Buscar por nombre de base..."
                                value={baseSearch}
                                onChange={(e) => setBaseSearch(e.target.value)}
                                className="pl-10 bg-white"
                                autoFocus
                            />
                        </div>
                        
                        {/* Lista de bases */}
                        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg">
                            {bases
                                .filter(b => b.aprobado)
                                .filter(b => {
                                    if (!baseSearch) return true;
                                    const searchLower = baseSearch.toLowerCase();
                                    const nombre = (b.nombre || '').toLowerCase();
                                    return nombre.includes(searchLower);
                                })
                                .map(b => {
                                    // Get muestra base info
                                    const muestra = muestrasBase.find(m => m.id === b.muestra_base_id);
                                    const marca = muestra ? marcas.find(m => m.id === muestra.marca_id)?.nombre : '';
                                    const tipo = muestra ? tiposProducto.find(t => t.id === muestra.tipo_producto_id)?.nombre : '';
                                    const tela = muestra ? telas.find(t => t.id === muestra.tela_id)?.nombre : '';
                                    
                                    return (
                                        <div
                                            key={b.id}
                                            onClick={() => {
                                                setFormData({...formData, base_id: b.id});
                                                setBaseModalOpen(false);
                                                setBaseSearch('');
                                            }}
                                            className={`p-3 border-b border-slate-100 last:border-0 cursor-pointer transition-colors ${
                                                formData.base_id === b.id 
                                                    ? 'bg-emerald-50 hover:bg-emerald-100' 
                                                    : 'hover:bg-slate-50'
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                                    formData.base_id === b.id 
                                                        ? 'border-emerald-500 bg-emerald-500' 
                                                        : 'border-slate-300'
                                                }`}>
                                                    {formData.base_id === b.id && (
                                                        <Check className="w-3 h-3 text-white" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-slate-800 text-sm">
                                                        {b.nombre}
                                                    </div>
                                                    {(marca || tipo || tela) && (
                                                        <div className="text-slate-500 text-xs mt-0.5">
                                                            {[marca, tipo, tela].filter(Boolean).join(' • ')}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            }
                            {bases.filter(b => b.aprobado).filter(b => {
                                if (!baseSearch) return true;
                                const searchLower = baseSearch.toLowerCase();
                                const nombre = (b.nombre || '').toLowerCase();
                                return nombre.includes(searchLower);
                            }).length === 0 && (
                                <div className="p-8 text-center text-slate-400">
                                    No se encontraron bases aprobadas
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="border-t border-slate-200 pt-4 flex justify-end">
                        <Button variant="outline" onClick={() => {
                            setBaseModalOpen(false);
                            setBaseSearch('');
                        }}>
                            Cancelar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={fichasDialogOpen} onOpenChange={setFichasDialogOpen}>
                <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader className="border-b border-slate-200 pb-4">
                        <DialogTitle className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                            Fichas Modelo - {currentModelo?.clasificacion || currentModelo?.nombre}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Gestionar fichas del modelo seleccionado
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto py-4 space-y-4">
                        {/* Formulario para crear nueva ficha */}
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                            <Button 
                                type="button"
                                onClick={() => setShowNewFichaForm(!showNewFichaForm)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white mb-3"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Crear Nueva Ficha
                            </Button>
                            
                            {showNewFichaForm && (
                                <div className="space-y-3 mt-3 pt-3 border-t border-slate-200">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <Label className="text-sm text-slate-600">Nombre de Ficha <span className="text-red-500">*</span></Label>
                                            <Input 
                                                placeholder="Ej: Ficha de Medidas"
                                                value={fichaName}
                                                onChange={(e) => setFichaName(e.target.value)}
                                                className="bg-white"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-sm text-slate-600">Archivo</Label>
                                            <div 
                                                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md bg-white cursor-pointer hover:bg-slate-50"
                                                onClick={() => fichaInputRef.current?.click()}
                                            >
                                                <Upload className="w-4 h-4 text-slate-400" />
                                                <span className="text-sm text-slate-500 truncate">
                                                    {fichaFile ? fichaFile.name : 'Seleccionar archivo'}
                                                </span>
                                            </div>
                                            <input 
                                                type="file" 
                                                ref={fichaInputRef} 
                                                onChange={(e) => setFichaFile(e.target.files?.[0] || null)}
                                                className="hidden" 
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button 
                                            type="button"
                                            onClick={handleUploadFicha}
                                            disabled={!fichaName || !fichaFile || uploadingFicha}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                            {uploadingFicha ? 'Guardando...' : 'Guardar Ficha'}
                                        </Button>
                                        <Button 
                                            type="button"
                                            variant="ghost"
                                            onClick={() => {
                                                setShowNewFichaForm(false);
                                                setFichaName('');
                                                setFichaFile(null);
                                            }}
                                        >
                                            Cancelar
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Campo de búsqueda */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input 
                                placeholder="Buscar por nombre..."
                                value={fichaSearch}
                                onChange={(e) => setFichaSearch(e.target.value)}
                                className="pl-10 bg-white"
                            />
                        </div>

                        {/* Tabla de fichas */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3 w-12">#</TableHead>
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3">Nombre de Ficha</TableHead>
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3 w-32">Archivo</TableHead>
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3 w-24">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredFichasModelo().length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                                                No hay fichas registradas
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredFichasModelo().map((ficha, idx) => (
                                            <TableRow key={ficha.index} className="border-b border-slate-100 hover:bg-slate-50">
                                                <TableCell className="py-2 px-3 text-sm text-slate-600">{idx + 1}</TableCell>
                                                <TableCell className="py-2 px-3 text-sm text-slate-700 font-medium">
                                                    {ficha.nombre}
                                                </TableCell>
                                                <TableCell className="py-2 px-3 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs px-2">
                                                            {getFileExtension(ficha.archivo)}
                                                        </Badge>
                                                        <a 
                                                            href={getFileUrl(ficha.archivo)} 
                                                            target="_blank" 
                                                            rel="noreferrer"
                                                            className="text-slate-400 hover:text-slate-600"
                                                            title="Descargar"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </a>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-2 px-3">
                                                    <button 
                                                        onClick={() => handleDeleteFicha(ficha.index)} 
                                                        className="p-1.5 hover:bg-red-100 rounded transition-colors"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-500" />
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                    <div className="border-t border-slate-200 pt-4 flex justify-end">
                        <Button variant="outline" onClick={() => setFichasDialogOpen(false)}>Cerrar</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog para Fichas Generales de la Base */}
            <Dialog open={baseFichasDialog} onOpenChange={setBaseFichasDialog}>
                <DialogContent className="sm:max-w-2xl bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderOpen className="w-5 h-5 text-purple-600" />
                            Fichas Generales - Base: {getBaseName(viewingModelo?.base_id)}
                        </DialogTitle>
                        <DialogDescription className="sr-only">Fichas generales de la base</DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-slate-500 mb-4">
                            Fichas generales asociadas a la base de este modelo (solo lectura)
                        </p>
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {viewingModelo?.base_fichas_archivos?.map((archivo, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 border rounded-lg hover:bg-slate-100 transition-colors">
                                    <span className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-purple-600" />
                                        <span className="font-medium">{viewingModelo.base_fichas_nombres?.[i] || `Ficha ${i + 1}`}</span>
                                    </span>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => window.open(getFileUrl(archivo), '_blank')}
                                        className="text-purple-600 hover:text-purple-800"
                                    >
                                        <Download className="w-4 h-4 mr-1" />Descargar
                                    </Button>
                                </div>
                            ))}
                            {(!viewingModelo?.base_fichas_archivos?.length) && (
                                <p className="text-center text-slate-400 py-8">No hay fichas generales en esta base</p>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button variant="outline" onClick={() => setBaseFichasDialog(false)}>Cerrar</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog para Tizados de la Base */}
            <Dialog open={baseTizadosDialog} onOpenChange={(open) => { setBaseTizadosDialog(open); if (!open) setTizadoSearch(''); }}>
                <DialogContent className="sm:max-w-4xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader className="border-b border-slate-200 pb-4">
                        <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                            <Scissors className="w-5 h-5 text-orange-600" />
                            Tizados - Base: {getBaseName(viewingModelo?.base_id)}
                        </DialogTitle>
                        <DialogDescription className="sr-only">Tizados de la base</DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto py-4 space-y-4">
                        <p className="text-sm text-slate-500">
                            Tizados asociados a la base de este modelo (solo lectura)
                        </p>
                        
                        {/* Buscador */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input 
                                placeholder="Buscar por ancho, curva u otras bases..."
                                value={tizadoSearch}
                                onChange={(e) => setTizadoSearch(e.target.value)}
                                className="pl-10 bg-white"
                            />
                        </div>

                        {/* Tabla de tizados */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3 w-28">Ancho</TableHead>
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3 w-32">Curva</TableHead>
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3">Otras Bases</TableHead>
                                        <TableHead className="text-slate-500 uppercase text-xs tracking-wider font-semibold py-2 px-3 w-28">Archivo</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTizados.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                                                No hay tizados disponibles
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredTizados.map((tizado) => {
                                            const otherBases = getOtherBasesForTizado(tizado);
                                            return (
                                                <TableRow key={tizado.id} className="border-b border-slate-100 hover:bg-slate-50 bg-emerald-50">
                                                    <TableCell className="py-2 px-3 text-sm text-slate-600">
                                                        {tizado.ancho ? `${tizado.ancho} cm` : '-'}
                                                    </TableCell>
                                                    <TableCell className="py-2 px-3 text-sm text-slate-600">
                                                        {tizado.curva || '-'}
                                                    </TableCell>
                                                    <TableCell className="py-2 px-3 text-sm">
                                                        <div className="flex flex-wrap gap-1">
                                                            {otherBases.length > 0 ? (
                                                                <>
                                                                    {otherBases.slice(0, 3).map((name, i) => (
                                                                        <Badge key={i} variant="outline" className="text-xs truncate max-w-[100px]">
                                                                            {name}
                                                                        </Badge>
                                                                    ))}
                                                                    {otherBases.length > 3 && (
                                                                        <Badge variant="secondary" className="text-xs">+{otherBases.length - 3}</Badge>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="text-slate-400 text-xs">Solo esta base</span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="py-2 px-3">
                                                        {tizado.archivo_tizado ? (
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={() => window.open(getFileUrl(tizado.archivo_tizado), '_blank')}
                                                                className="h-7 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                                            >
                                                                <Download className="w-3 h-3 mr-1" />
                                                                Ver
                                                            </Button>
                                                        ) : (
                                                            <span className="text-slate-400 text-xs">Sin archivo</span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                    <div className="border-t border-slate-200 pt-4 flex justify-end">
                        <Button variant="outline" onClick={() => setBaseTizadosDialog(false)}>Cerrar</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ModelosPage;
